import $ from 'jquery';

/**
 * ============================================================================
 * VIDEO POPUP
 * ============================================================================
 * Shared player popup (markup: layouts/components/video/popup.blade.php).
 *
 * Trigger: any element with [data-video-popup-open] and either
 *   - data-video-url (+ optional data-video-title, data-video-download,
 *     data-video-poster), or
 *   - data-video-items='[{"url": "...", "title": "...", "download": "...", "poster": "..."}]'
 *     for several videos, which are shown as tabs.
 *
 * The <video> is created on open and destroyed on close, so nothing is loaded
 * with the page and playback always stops. Showing/hiding the popup itself is
 * shared with popup.js (same [data-popup-close]/[data-popup-overlay]/Esc).
 *
 * Construct it as soon as the script runs, not on DOM ready: the click
 * listener is on document, so a click made while the page is still loading
 * isn't lost. The popup markup is looked up lazily, on the first open.
 */
export default class VideoPopup {
	constructor() {
		this.popup = null;
		this.player = null;
		this.items = [];
		this.trigger = null;

		// [data-video-inline-play] is the legacy trigger still used in the
		// admin-managed About page HTML; drop it once that content is updated.
		document.addEventListener('click', (e) => {
			const button = e.target.closest('[data-video-popup-open], [data-video-inline-play]');

			if (button) {
				e.preventDefault();
				this.whenReady(() => this.open(button));
			}
		});

		document.addEventListener('keydown', (e) => {
			if (e.key === 'Escape' && this.player && this.player.firstChild) {
				this.destroy();
			}
		});
	}

	// Runs the callback once the popup markup is available: right away, or
	// after DOMContentLoaded when the click came before the popup was parsed.
	whenReady(callback) {
		if (this.init()) {
			callback();

			return;
		}

		if (document.readyState === 'loading') {
			document.addEventListener('DOMContentLoaded', () => {
				if (this.init()) {
					callback();
				}
			}, { once: true });
		}
	}

	init() {
		if (this.popup) return true;

		const popup = document.querySelector('[data-video-popup]');

		if (!popup) return false;

		this.popup = popup;
		this.player = popup.querySelector('[data-video-popup-player]');
		this.tabs = popup.querySelector('[data-video-popup-tabs]');
		this.footer = popup.querySelector('[data-video-popup-footer]');
		this.title = popup.querySelector('[data-video-popup-title]');
		this.download = popup.querySelector('[data-video-popup-download]');

		this.tabs.addEventListener('click', (e) => {
			const tab = e.target.closest('[data-video-popup-tab]');

			if (tab) {
				this.select(Number(tab.dataset.videoPopupTab));
			}
		});

		popup.querySelectorAll('[data-popup-close], [data-popup-overlay]').forEach((node) => {
			node.addEventListener('click', () => this.destroy());
			node.addEventListener('touchend', () => this.destroy());
		});

		return true;
	}

	open(button) {
		this.destroy();
		this.trigger = button;
		this.items = this.parseItems(button);

		if (!this.items.length) return;

		this.renderTabs();
		this.select(0);

		$('[data-popup]').hide();
		$(this.popup).fadeIn();
		document.documentElement.classList.add('is-popup-open');
	}

	parseItems(button) {
		const { videoItems, videoUrl, videoTitle, videoDownload, videoPoster } = button.dataset;

		if (videoItems) {
			try {
				return JSON.parse(videoItems).filter((item) => item && item.url);
			} catch (e) {
				return [];
			}
		}

		return videoUrl ? [{ url: videoUrl, title: videoTitle, download: videoDownload, poster: videoPoster }] : [];
	}

	// Tabs are only shown when there is more than one video to switch between.
	renderTabs() {
		this.tabs.innerHTML = '';
		this.tabs.classList.toggle('d-none', this.items.length < 2);

		if (this.items.length < 2) return;

		this.items.forEach((item, index) => {
			const tab = document.createElement('button');

			tab.type = 'button';
			tab.className = 'popup__tab';
			tab.dataset.videoPopupTab = String(index);
			tab.textContent = item.title || `Video ${index + 1}`;
			this.tabs.appendChild(tab);
		});
	}

	select(index) {
		const item = this.items[index];

		if (!item) return;

		this.tabs.querySelectorAll('[data-video-popup-tab]').forEach((tab) => {
			const isActive = Number(tab.dataset.videoPopupTab) === index;

			tab.classList.toggle('is-active', isActive);
			tab.setAttribute('aria-selected', String(isActive));
		});

		this.renderTitle(item);
		this.renderDownload(item);
		this.stopVideo();

		const video = this.createVideo(item.url, item.poster);

		this.player.innerHTML = '';
		this.player.appendChild(video);
		this.autoplay(video);
	}

	// Title in the header, in place of the tabs: with several videos the title
	// is already shown in the active tab, so it is not repeated.
	renderTitle(item) {
		const hasTitle = Boolean(item.title) && this.items.length < 2;

		this.title.textContent = hasTitle ? item.title : '';
		this.title.classList.toggle('d-none', !hasTitle);
	}

	// The footer holds only the download button and is hidden without a link.
	renderDownload(item) {
		const hasDownload = Boolean(item.download);

		if (hasDownload) {
			this.download.setAttribute('href', item.download);
		} else {
			this.download.removeAttribute('href');
		}

		this.download.classList.toggle('d-none', !hasDownload);
		this.footer.classList.toggle('d-none', !hasDownload);
	}

	// The poster (the card thumbnail) is shown until the first frame is ready.
	createVideo(url, poster) {
		const video = document.createElement('video');

		video.src = url;

		if (poster) {
			video.poster = poster;
		}

		video.controls = true;
		video.playsInline = true;
		video.preload = 'auto';

		return video;
	}

	// Autoplay with sound may be blocked (mostly on mobile): retry muted, the
	// native controls stay visible so the user can unmute.
	autoplay(video) {
		const attempt = video.play();

		if (attempt && typeof attempt.catch === 'function') {
			attempt.catch(() => {
				video.muted = true;
				video.play().catch(() => {});
			});
		}
	}

	stopVideo() {
		const video = this.player.querySelector('video');

		if (video) {
			video.pause();
			video.removeAttribute('src');
			video.load();
		}
	}

	destroy() {
		if (!this.player || !this.player.firstChild) return;

		this.stopVideo();
		this.player.innerHTML = '';
		this.tabs.innerHTML = '';
		this.items = [];
		document.documentElement.classList.remove('is-popup-open');

		if (this.trigger) {
			this.trigger.focus({ preventScroll: true });
			this.trigger = null;
		}
	}
}
